import { supabase } from './supabase';

// ─── Types ───────────────────────────────────────────────────

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';
export type LogCategory = 'functional' | 'technical' | 'error';

export interface LogEntry {
  id: string;
  level: LogLevel;
  category: LogCategory;
  action: string;
  source: string;
  details: Record<string, any>;
  userId?: string;
  createdAt: Date;
}

type LogListener = (entry: LogEntry) => void;

// ─── In-memory store + pub/sub ───────────────────────────────

const MAX_MEMORY = 500;
const memoryStore: LogEntry[] = [];
const listeners: Set<LogListener> = new Set();

export function subscribe(fn: LogListener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getMemoryLogs(): readonly LogEntry[] {
  return memoryStore;
}

function pushToMemory(entry: LogEntry) {
  memoryStore.push(entry);
  if (memoryStore.length > MAX_MEMORY) memoryStore.shift();
  listeners.forEach((fn) => {
    try { fn(entry); } catch { /* listener error – ignore */ }
  });
}

// ─── localStorage fallback ───────────────────────────────────

const LS_KEY = 'hammerjob_offline_logs';
const MAX_LS_ENTRIES = 200;

function saveToLocalStorage(entries: LogEntry[]) {
  try {
    const existing = loadFromLocalStorage();
    const merged = [...existing, ...entries].slice(-MAX_LS_ENTRIES);
    localStorage.setItem(LS_KEY, JSON.stringify(merged.map((e) => ({
      level: e.level,
      category: e.category,
      action: e.action,
      source: e.source,
      details: e.details,
      userId: e.userId,
      createdAt: e.createdAt.toISOString(),
    }))));
  } catch { /* localStorage full or unavailable */ }
}

function loadFromLocalStorage(): LogEntry[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((r: any, i: number) => ({
      id: `ls-${i}-${Date.now()}`,
      level: r.level,
      category: r.category,
      action: r.action,
      source: r.source,
      details: r.details || {},
      userId: r.userId,
      createdAt: new Date(r.createdAt),
    }));
  } catch {
    return [];
  }
}

function clearLocalStorage() {
  try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
}

// ─── DB buffer + flush ───────────────────────────────────────

const FLUSH_INTERVAL_MS = 5_000;
const FLUSH_THRESHOLD = 20;
let dbBuffer: LogEntry[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let consecutiveFailures = 0;

async function drainLocalStorage(userId: string | undefined) {
  const offlineLogs = loadFromLocalStorage();
  if (offlineLogs.length === 0) return;

  const rows = offlineLogs.map((e) => ({
    level: e.level,
    category: e.category,
    action: e.action,
    source: e.source,
    details: { ...e.details, _recovered: true },
    user_id: userId || e.userId || null,
    created_at: e.createdAt.toISOString(),
  }));

  const { error } = await supabase.from('admin_logs').insert(rows);
  if (!error) {
    clearLocalStorage();
    console.log(`[logger] recovered ${rows.length} offline logs`);
  }
}

async function flushToDb() {
  if (dbBuffer.length === 0 && consecutiveFailures === 0) return;

  const batch = dbBuffer.splice(0, dbBuffer.length);
  let userId: string | undefined;

  try {
    const { data: { session } } = await supabase.auth.getSession();
    userId = session?.user?.id;
  } catch { /* no session */ }

  if (batch.length === 0) {
    if (consecutiveFailures > 0) {
      await drainLocalStorage(userId);
      consecutiveFailures = 0;
    }
    return;
  }

  const rows = batch.map((e) => ({
    level: e.level,
    category: e.category,
    action: e.action,
    source: e.source,
    details: e.details,
    user_id: userId || e.userId || null,
    created_at: e.createdAt.toISOString(),
  }));

  const { error } = await supabase.from('admin_logs').insert(rows);
  if (error) {
    consecutiveFailures++;
    saveToLocalStorage(batch);
    console.warn(`[logger] flush failed (#${consecutiveFailures}), ${batch.length} entries saved to localStorage:`, error.message);
  } else {
    if (consecutiveFailures > 0) {
      await drainLocalStorage(userId);
    }
    consecutiveFailures = 0;
  }
}

function ensureFlushTimer() {
  if (flushTimer) return;
  flushTimer = setInterval(() => {
    flushToDb();
  }, FLUSH_INTERVAL_MS);
}

// ─── Core log function ───────────────────────────────────────

let idCounter = 0;

function log(level: LogLevel, category: LogCategory, action: string, source: string, details?: Record<string, any>) {
  const entry: LogEntry = {
    id: `${Date.now()}-${++idCounter}`,
    level,
    category,
    action,
    source,
    details: details || {},
    createdAt: new Date(),
  };

  const tag = `[${level.toUpperCase()}][${category}]`;
  const msg = `${tag} ${source} > ${action}`;
  if (level === 'error') {
    console.error(msg, entry.details);
  } else if (level === 'warn') {
    console.warn(msg, entry.details);
  } else {
    console.log(msg, entry.details);
  }

  pushToMemory(entry);

  dbBuffer.push(entry);
  if (dbBuffer.length >= FLUSH_THRESHOLD) {
    flushToDb();
  }
  ensureFlushTimer();
}

// ─── Public API ──────────────────────────────────────────────

export const logger = {
  functional(action: string, source: string, details?: Record<string, any>) {
    log('info', 'functional', action, source, details);
  },
  technical(action: string, source: string, details?: Record<string, any>) {
    log('debug', 'technical', action, source, details);
  },
  error(action: string, source: string, details?: Record<string, any>) {
    log('error', 'error', action, source, details);
  },
  warn(action: string, source: string, details?: Record<string, any>) {
    log('warn', 'technical', action, source, details);
  },
  info(action: string, source: string, details?: Record<string, any>) {
    log('info', 'technical', action, source, details);
  },
  flush: flushToDb,
};

// ─── Offline logs accessor (for Logs page) ──────────────────

export function getOfflineLogs(): readonly LogEntry[] {
  return loadFromLocalStorage();
}

export function getOfflineCount(): number {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return 0;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.length : 0;
  } catch {
    return 0;
  }
}

// ─── loggedQuery helper ──────────────────────────────────────

export async function loggedQuery<T>(
  source: string,
  action: string,
  queryFn: () => Promise<{ data: T; error: any }>
): Promise<{ data: T; error: any }> {
  const result = await queryFn();
  if (result.error) {
    logger.error(action, source, {
      error: result.error.message,
      code: result.error.code,
      hint: result.error.hint,
    });
  } else {
    logger.technical(`${action}_ok`, source, {
      rowCount: Array.isArray(result.data) ? result.data.length : result.data ? 1 : 0,
    });
  }
  return result;
}

// ─── DB log fetcher (for Logs page) ─────────────────────────

export interface LogsFilter {
  level?: LogLevel;
  category?: LogCategory;
  source?: string;
  search?: string;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

export interface LogsPage {
  entries: LogEntry[];
  total: number;
}

export async function fetchLogsFromDb(filter: LogsFilter = {}): Promise<LogsPage> {
  const pageSize = filter.pageSize || 50;
  const page = filter.page || 0;

  let query = supabase
    .from('admin_logs')
    .select('*', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range(page * pageSize, (page + 1) * pageSize - 1);

  if (filter.level) query = query.eq('level', filter.level);
  if (filter.category) query = query.eq('category', filter.category);
  if (filter.source) query = query.eq('source', filter.source);
  if (filter.from) query = query.gte('created_at', filter.from);
  if (filter.to) query = query.lte('created_at', filter.to);
  if (filter.search) query = query.or(`action.ilike.%${filter.search}%,details->>'error'.ilike.%${filter.search}%`);

  const { data, error, count } = await query;
  if (error) throw error;

  return {
    entries: (data || []).map((r: any) => ({
      id: r.id,
      level: r.level,
      category: r.category,
      action: r.action,
      source: r.source,
      details: r.details || {},
      userId: r.user_id,
      createdAt: new Date(r.created_at),
    })),
    total: count || 0,
  };
}

export async function fetchLogSources(): Promise<string[]> {
  const { data } = await supabase
    .from('admin_logs')
    .select('source')
    .limit(500);

  const sources = new Set((data || []).map((r: any) => r.source));
  return [...sources].sort();
}
